import { useEffect, useRef } from "react";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import "intl-tel-input/build/js/utils";
import "./IntlPhoneInput.css";

type Iti = ReturnType<typeof intlTelInput>;

const DEFAULT_ERROR_MAP = [
  "Invalid number",
  "Invalid country code",
  "Phone number is too short",
  "Phone number is too long",
  "Invalid number",
];

export type TPhoneErrorLabels = {
  required?: string;
  invalidNumber?: string;
  invalidCountryCode?: string;
  tooShort?: string;
  tooLong?: string;
};

type TIntlPhoneInputProps = {
  placeholder?: string;
  error?: string;
  defaultValue?: string;
  defaultCountryCode?: string;
  defaultFullNumber?: string;
  errorLabels?: TPhoneErrorLabels;
  onPhoneChange: (
    fullNumber: string,
    localNumber: string,
    dialCode: string
  ) => void;
  onValidation: (error: string) => void;
};

function IntlPhoneInput({
  placeholder,
  error,
  defaultValue,
  defaultCountryCode,
  defaultFullNumber,
  errorLabels,
  onPhoneChange,
  onValidation,
}: TIntlPhoneInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const itiRef = useRef<Iti | null>(null);
  const onPhoneChangeRef = useRef(onPhoneChange);
  const onValidationRef = useRef(onValidation);
  const errorLabelsRef = useRef(errorLabels);

  onPhoneChangeRef.current = onPhoneChange;
  onValidationRef.current = onValidation;
  errorLabelsRef.current = errorLabels;

  useEffect(() => {
    if (!inputRef.current) return;

    const input = inputRef.current;

    const iti = intlTelInput(input, {
      initialCountry: "gb",
      preferredCountries: ["gb", "us", "ie"],
      countrySearch: true,
      separateDialCode: true,
      nationalMode: true,
      formatOnDisplay: false,
      customPlaceholder: () => placeholder || "Enter your phone number",
    });

    itiRef.current = iti;

    // Set initial value: full E.164 number takes priority, then countryCode+localNumber, then localNumber alone
    if (defaultFullNumber) {
      iti.setNumber(defaultFullNumber);
    } else if (defaultCountryCode && defaultValue) {
      iti.setNumber(defaultCountryCode + defaultValue);
    } else if (defaultValue) {
      iti.setNumber(defaultValue);
    }

    function syncValues() {
      const countryData = iti.getSelectedCountryData();
      const dialCode = countryData.dialCode ? `+${countryData.dialCode}` : "";
      const fullNumber = iti.getNumber(); // E.164 e.g. "+447123456789"
      const localNumber = dialCode && fullNumber.startsWith(dialCode)
        ? fullNumber.slice(dialCode.length)
        : input.value.replace(/[^0-9]/g, "");
      onPhoneChangeRef.current(fullNumber, localNumber, dialCode);
    }

    function validate() {
      const digits = input.value.replace(/[^0-9]/g, "");
      const labels = errorLabelsRef.current;
      const errorMap = [
        labels?.invalidNumber || DEFAULT_ERROR_MAP[0],
        labels?.invalidCountryCode || DEFAULT_ERROR_MAP[1],
        labels?.tooShort || DEFAULT_ERROR_MAP[2],
        labels?.tooLong || DEFAULT_ERROR_MAP[3],
        labels?.invalidNumber || DEFAULT_ERROR_MAP[4],
      ];

      if (digits.length === 0) {
        onValidationRef.current(labels?.required || "Phone number is required");
        return;
      }

      if (!iti.isValidNumber()) {
        const errorCode = iti.getValidationError();
        onValidationRef.current(errorMap[errorCode] || labels?.invalidNumber || "Invalid number");
        return;
      }

      onValidationRef.current("");
    }

    function handleInput() {
      input.value = input.value.replace(/[^0-9]/g, "");
      syncValues();
      validate();
    }

    function handleCountryChange() {
      syncValues();
      if (input.value.length > 0) {
        validate();
      }
    }

    function handleBlur() {
      validate();
    }

    input.addEventListener("input", handleInput);
    input.addEventListener("countrychange", handleCountryChange);
    input.addEventListener("blur", handleBlur);

    return () => {
      input.removeEventListener("input", handleInput);
      input.removeEventListener("countrychange", handleCountryChange);
      input.removeEventListener("blur", handleBlur);
      iti.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder]);

  return (
    <div className="m-form__group log-reg-form-group--phone" style={{ width: "100%" }}>
      <input
        ref={inputRef}
        type="tel"
        style={{ width: "100%" }}
        className={`m-form__input ${error ? "input-validation-error" : ""}`}
      />
      {error && (
        <span className="field-validation-error" style={{ marginBottom: 0 }}>
          {error}
        </span>
      )}
    </div>
  );
}

export default IntlPhoneInput;
